(function () {
  if (customElements.get('home-hero-bird')) return;

  var BREAKPOINT_QUERY = '(max-width: 1024.98px)';

  // TUNING NOTES (all of these are the knobs to adjust - no logic changes needed):
  // - `scale`: the steal/scared compositions are authored on a small 600x350 /
  //   370x270 canvas. This scales the whole flight-path animation up so its
  //   internal distances match the real screen. If the bird's start position
  //   isn't far enough left/off-screen, INCREASE this (it pushes the
  //   composition's origin further left because the marker point gets scaled
  //   too). If the bird overshoots past the E, decrease it.
  // - `nudge`: a small final pixel offset applied after scaling, for fine
  //   alignment of the flight path against the real "e".
  // - `mugScale` / `mugOffset`: the standalone mug (mugOnly.json) has no
  //   marker layer of its own, so it CANNOT reuse the `scale`/`nudge` above -
  //   it's positioned independently, directly relative to the "e" character's
  //   bottom-right corner. Tune `mugOffset` (px, relative to that corner) and
  //   `mugScale` (size of the mug graphic) purely by eye.
  // - `maxInterruptFrame`: clicks are ignored from this frame onward. The
  //   steal animation holds a steady "standing at the top of the E" pose for
  //   most of its runtime, then hops down to the mug in one motion (a single
  //   sharp jump, on the real desktop asset around frame 64) before flying
  //   off with it. The scared animation's own frame 0 pose matches that same
  //   "standing at the top" pose closely, so keeping this cutoff BEFORE the
  //   hop means an interrupt always lands while the bird is still in a pose
  //   that roughly matches where the scared animation picks up - hence no
  //   more vertical jump on interrupt, without needing to fake a drop offset.
  var HOME_HERO_BIRD_CONFIG = {
    desktop: {
      width: 600,
      height: 350,
      steal: { totalFrames: 101 },
      scared: { totalFrames: 88 },
      maxInterruptFrame: 62,
      scale: 0.78,
      nudge: { x: -178, y: -91 },
      mugScale: 0.76,
      mugOffset: { x: -373, y: -248 },
      hitZoneHeight: 220,
    },
    mobile: {
      width: 370,
      height: 270,
      steal: { totalFrames: 101 },
      scared: { totalFrames: 88 },
      maxInterruptFrame: 62,
      scale: 0.8,
      nudge: { x: -106, y: -51 },
      mugScale: 0.79,
      mugOffset: { x: -233, y: -190 },
      hitZoneHeight: 160,
    },
  };

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  // `navbar--scrolled` (toggled on <body> by src/js/includes/navbar.js on
  // scroll) shrinks .title-fixed from 148px down to 48px, which invalidates
  // every calibrated position/scale value - the bird and mug only make
  // sense while the wordmark is at full size.
  function isNavbarScrolled() {
    return document.body.classList.contains('navbar--scrolled');
  }

  // On mobile `.title-fixed` is position:static (it scrolls away with the
  // page), unlike desktop's position:fixed wordmark - so the calibrated "e"
  // position goes stale on the very first pixel of scroll, not just past a
  // threshold. `navbar--scrolled`/`scrolled-down` (src/js/includes/navbar.js)
  // are debounced and threshold/direction based, built for the navbar's own
  // needs, so mobile checks the real scroll position directly instead.
  function isPageScrolled() {
    return window.scrollY > 0;
  }

  function fetchLottieJSON(url) {
    return fetch(url).then(function (res) {
      return res.json();
    });
  }

  // Both the steal and scared compositions (per breakpoint) contain a
  // hidden null layer (ty:3) marking where the "ACME" wordmark registers
  // within the composition's coordinate space - see plan for details.
  function findMarkerLayer(lottieData) {
    var layers = lottieData.layers || [];
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].ty === 3) return layers[i];
    }
    return null;
  }

  function resolveMarkerPoint(marker) {
    var p = marker.ks.p.k;
    var a = marker.ks.a.k;
    var s = marker.ks.s.k;
    var scaleX = s[0] / 100;
    var scaleY = s[1] / 100;
    return {
      x: p[0] - a[0] * scaleX,
      y: p[1] - a[1] * scaleY,
    };
  }

  // Maps the marker's resolved composition-space point onto the real
  // ".title-fixed" "e" character's bottom-right corner, accounting for the
  // composition being rendered at `scale`x its native size. Only valid for
  // the steal/scared compositions, which share this null-layer marker - NOT
  // for the standalone mug (see positionMugStage).
  function transformFromResolved(resolved, eRect, nudge, scale) {
    if (!resolved) {
      return { translateX: eRect.right, translateY: eRect.bottom };
    }
    return {
      translateX: eRect.right - resolved.x * scale + (nudge ? nudge.x : 0),
      translateY: eRect.bottom - resolved.y * scale + (nudge ? nudge.y : 0),
    };
  }

  function positionStage(container, transform, width, height, scale) {
    container.style.left = transform.translateX + 'px';
    container.style.top = transform.translateY + 'px';
    container.style.width = width * scale + 'px';
    container.style.height = height * scale + 'px';
  }

  // The mug composition has no marker layer, and a shape layer's own
  // transform doesn't reliably indicate where its drawn artwork sits (unlike
  // a null layer, which has no geometry and so its position IS the marker).
  // Position it independently, directly relative to the "e" corner.
  function positionMugStage(container, eRect, config) {
    container.style.width = config.width * config.mugScale + 'px';
    container.style.height = config.height * config.mugScale + 'px';
    container.style.left = eRect.right + config.mugOffset.x + 'px';
    container.style.top = eRect.bottom + config.mugOffset.y + 'px';
  }

  class HomeHeroBirdElement extends HTMLElement {
    constructor() {
      super();

      this.state = 'idle-watch';
      this.isMobile = null;
      this.mugStolen = false;
      this.hasBeenScared = false;
      this.logoVisible = true;
      this.stealAnim = null;
      this.scaredAnim = null;
      this.mugAnim = null;
      this.eRect = null;
      this.transform = null;
      this.mobileScrollRaf = null;
      this.activeConfig = null;
      this.mugDataPromise = null;

      this.enabled = this.dataset.enabled === 'true';
      this.idleSecondsInitial = parseInt(this.dataset.idleSecondsInitial, 10) || 30;
      this.idleSeconds = parseInt(this.dataset.idleSeconds, 10) || 30;
      this.assets = {
        desktop: {
          steal: this.dataset.stealDesktop,
          scared: this.dataset.scaredDesktop,
          mug: this.dataset.mugDesktop,
        },
        mobile: {
          steal: this.dataset.stealMobile,
          scared: this.dataset.scaredMobile,
          mug: this.dataset.mugMobile,
        },
      };

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.enabled || reducedMotion) return;

      this.logo = this.querySelector('.title-fixed');
      this.stage = this.querySelector('[data-role="bird-stage"]');
      this.mugStage = this.querySelector('[data-role="mug-stage"]');
      this.hitZone = this.querySelector('[data-role="hit-zone"]');

      if (!this.logo || !this.stage || !this.mugStage || !this.hitZone) return;

      this.mq = window.matchMedia(BREAKPOINT_QUERY);
      this.isMobile = this.mq.matches;
      this.navbarScrolled = isNavbarScrolled();
      this.mobileScrolled = this.isMobile && isPageScrolled();

      this.onActivity = this.resetIdleTimer.bind(this);
      this.onResize = debounce(this.handleResize.bind(this), 150);
      this.onHitZoneClick = this.handleHitZoneClick.bind(this);
      this.onVisibility = this.handleVisibility.bind(this);
      this.onMqChange = this.onResize;
      this.onNavbarScrollChange = this.handleNavbarScrollChange.bind(this);
      this.onMobileScrollChange = this.handleMobileScrollChange.bind(this);

      this.visibilityObserver = new IntersectionObserver(this.onVisibility, { threshold: 0 });
      this.visibilityObserver.observe(this.logo);

      this.navbarScrollObserver = new MutationObserver(this.onNavbarScrollChange);
      this.navbarScrollObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach((evt) => {
        window.addEventListener(evt, this.onActivity, { passive: true });
      });
      window.addEventListener('scroll', this.onMobileScrollChange, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.mq.addEventListener('change', this.onMqChange);
      this.hitZone.addEventListener('click', this.onHitZoneClick);

      this.locateE();
      this.positionHitZone();
      this.loadInitialMug();
      this.resetIdleTimer();
    }

    disconnectedCallback() {
      this.teardown();
    }

    handleVisibility(entries) {
      this.logoVisible = entries[0].isIntersecting;
    }

    handleNavbarScrollChange() {
      if (this.isMobile) return; // mobile uses handleMobileScrollChange instead
      var scrolled = isNavbarScrolled();
      if (scrolled === this.navbarScrolled) return; // unrelated body class change
      this.navbarScrolled = scrolled;

      if (scrolled) {
        // The wordmark just shrank - drop everything until it's back to
        // full size, since the calibrated positioning no longer applies.
        this.destroyBirdAnims();
        if (this.mugAnim) {
          this.mugAnim.destroy();
          this.mugAnim = null;
        }
        this.mugStage.classList.remove('is-visible');
        this.mugStage.innerHTML = '';
        this.state = 'idle-watch';
        this.resetIdleTimer();
      } else {
        // .title-fixed has its own CSS transition (font-size 48px -> 148px),
        // so the "e" character's real bounding box isn't final until that
        // finishes - measuring immediately would grab a mid-transition size.
        this.waitForTitleTransition(() => {
          if (this.navbarScrolled) return; // scrolled again before this fired
          this.locateE();
          this.positionHitZone();
          this.loadInitialMug();
        });
        this.resetIdleTimer();
      }
    }

    // Mobile's `.title-fixed` is position:static, so it moves continuously
    // with the page as the user scrolls - rather than hiding the mug the
    // instant any scroll happens (which popped it in/out even while the
    // wordmark was still fully visible), keep tracking its position on every
    // scroll frame so the mug stays visually pinned to the "e". The bird
    // animations still stop the moment scrolling starts, since a moving
    // flight path/interrupt target wouldn't make sense.
    handleMobileScrollChange() {
      if (!this.isMobile || this.mobileScrollRaf) return;
      this.mobileScrollRaf = requestAnimationFrame(() => {
        this.mobileScrollRaf = null;
        this.updateMobileScroll();
      });
    }

    updateMobileScroll() {
      var scrolled = isPageScrolled();
      var wasScrolled = this.mobileScrolled;
      this.mobileScrolled = scrolled;

      if (scrolled && !wasScrolled && this.state !== 'idle-watch' && this.state !== 'done') {
        this.resetSequence(); // stop the animation - the target it's flying to just started moving
      }

      this.locateE();
      this.positionHitZone();
      if (this.mugStage.classList.contains('is-visible')) {
        positionMugStage(this.mugStage, this.eRect, this.activeBreakpointConfig());
      }
    }

    waitForTitleTransition(callback) {
      var done = false;
      var onEnd = (e) => {
        if (e.target !== this.logo || e.propertyName !== 'font-size') return;
        finish();
      };
      var finish = () => {
        if (done) return;
        done = true;
        this.logo.removeEventListener('transitionend', onEnd);
        callback();
      };
      this.logo.addEventListener('transitionend', onEnd);
      // Fallback in case transitionend never fires (already at target size, etc).
      setTimeout(finish, 350);
    }

    activeBreakpointConfig() {
      return this.isMobile ? HOME_HERO_BIRD_CONFIG.mobile : HOME_HERO_BIRD_CONFIG.desktop;
    }

    activeAssets() {
      return this.isMobile ? this.assets.mobile : this.assets.desktop;
    }

    locateE() {
      if (!this.logo) return;
      var textNode = null;
      for (var i = 0; i < this.logo.childNodes.length; i++) {
        if (this.logo.childNodes[i].nodeType === Node.TEXT_NODE) {
          textNode = this.logo.childNodes[i];
          break;
        }
      }
      if (!textNode) return;

      var text = textNode.textContent;
      var eIndex = text.replace(/\s+$/, '').length - 1;
      if (eIndex < 0) return;

      var range = document.createRange();
      range.setStart(textNode, eIndex);
      range.setEnd(textNode, eIndex + 1);
      this.eRect = range.getBoundingClientRect();
    }

    positionHitZone() {
      if (!this.hitZone || !this.eRect) return;
      var config = this.activeBreakpointConfig();
      var bandHeight = config.hitZoneHeight;
      this.hitZone.style.left = '0px';
      this.hitZone.style.width = Math.max(this.eRect.right, 0) + 'px';
      this.hitZone.style.top = this.eRect.top - bandHeight / 2 + 'px';
      this.hitZone.style.height = bandHeight + 'px';
    }

    // Before the bird has ever been scared off, idle periods use
    // `idleSecondsInitial` - once it's been interrupted at least once
    // (see completeSequence), every later idle period uses `idleSeconds`
    // instead, letting merchants set a longer "first appearance" delay and
    // a shorter (or longer) "keeps coming back" delay.
    currentIdleSeconds() {
      return this.hasBeenScared ? this.idleSeconds : this.idleSecondsInitial;
    }

    resetIdleTimer() {
      if (this.state !== 'idle-watch') return;
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => this.tryStartSequence(), this.currentIdleSeconds() * 1000);
    }

    tryStartSequence() {
      if (this.state !== 'idle-watch') return;
      if (this.isMobile ? this.mobileScrolled : this.navbarScrolled) {
        this.resetIdleTimer();
        return;
      }
      if (!this.logoVisible) {
        this.resetIdleTimer();
        return;
      }

      this.isMobile = this.mq.matches;
      this.startSteal();
    }

    async startSteal() {
      var config = this.activeBreakpointConfig();
      var assets = this.activeAssets();
      if (!assets.steal) return;

      this.state = 'stealing';
      this.activeConfig = config;

      if (assets.mug) this.mugDataPromise = fetchLottieJSON(assets.mug);

      var stealData;
      try {
        stealData = await fetchLottieJSON(assets.steal);
      } catch (e) {
        this.state = 'idle-watch';
        this.resetIdleTimer();
        return;
      }
      if (this.state !== 'stealing') return; // aborted while fetching

      var marker = findMarkerLayer(stealData);
      this.markerResolved = marker ? resolveMarkerPoint(marker) : null;
      this.transform = transformFromResolved(this.markerResolved, this.eRect, config.nudge, config.scale);
      positionStage(this.stage, this.transform, config.width, config.height, config.scale);

      this.stealAnim = lottie.loadAnimation({
        container: this.stage,
        renderer: 'svg',
        loop: false,
        autoplay: true,
        animationData: stealData,
      });

      // Only now hide the persistently-shown static mug (from page load or
      // a previous interrupt) - waiting until the steal composition has
      // actually rendered its own built-in mug means the two overlap
      // instead of leaving a gap where neither is visible.
      this.stealAnim.addEventListener('DOMLoaded', () => {
        if (this.state !== 'stealing') return;
        if (this.mugAnim) {
          this.mugAnim.destroy();
          this.mugAnim = null;
        }
        this.mugStage.classList.remove('is-visible');
        this.mugStage.innerHTML = '';
      });

      this.stealAnim.addEventListener('complete', () => {
        if (this.state === 'stealing') this.completeSequence(true);
      });
    }

    handleHitZoneClick() {
      if (this.state !== 'stealing' || !this.stealAnim) return;
      if (this.stealAnim.currentFrame >= this.activeConfig.maxInterruptFrame) return; // too close to the hop down, let it play out
      this.interrupt();
    }

    async interrupt() {
      this.state = 'interrupted';

      // Freeze the steal animation (its own built-in mug stays visible) and
      // bring in the static mug on top of it BEFORE tearing the steal
      // composition down, so the two overlap instead of leaving a gap.
      this.stealAnim.goToAndStop(this.stealAnim.currentFrame, true);

      await this.showMug();
      if (this.state !== 'interrupted') return; // aborted mid-transition

      if (this.stealAnim) {
        this.stealAnim.destroy();
        this.stealAnim = null;
      }
      this.playScaredFrom();
    }

    async showMug() {
      if (!this.mugDataPromise) return;
      var mugData;
      try {
        mugData = await this.mugDataPromise;
      } catch (e) {
        return;
      }
      if (this.state !== 'interrupted') return;
      this.renderStaticMug(mugData);
    }

    // Loads and pins the static mug composition at the "e" corner. Shared by
    // the page-load mug and the post-interrupt mug. Always instant (no fade)
    // - every call site already overlaps with something else on screen (the
    // page itself, or a frozen steal frame) at the moment it appears, so
    // there's nothing a fade would soften.
    renderStaticMug(mugData) {
      var config = this.activeBreakpointConfig();
      if (this.mugAnim) {
        this.mugAnim.destroy();
        this.mugAnim = null;
      }
      positionMugStage(this.mugStage, this.eRect, config);
      this.mugAnim = lottie.loadAnimation({
        container: this.mugStage,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        animationData: mugData,
      });
      this.mugAnim.goToAndStop(0, true);
      this.mugStage.classList.add('is-visible');
    }

    // Shows the mug pinned at the "e" corner as soon as the page loads, so
    // it's already sitting there instead of flashing in once the steal
    // animation's own built-in mug first appears.
    loadInitialMug() {
      // Desktop hides the mug entirely while navbar--scrolled (see
      // handleNavbarScrollChange) - mobile keeps it visible at all scroll
      // positions (see updateMobileScroll), so it has no equivalent guard here.
      if (this.mugStolen || (!this.isMobile && isNavbarScrolled())) return;
      var assets = this.activeAssets();
      if (!assets.mug) return;
      fetchLottieJSON(assets.mug).then((mugData) => {
        if (this.mugStolen || this.state !== 'idle-watch') return;
        this.renderStaticMug(mugData);
      });
    }

    playScaredFrom() {
      this.state = 'scared';
      var config = this.activeConfig;
      var assets = this.activeAssets();

      this.scaredAnim = lottie.loadAnimation({
        container: this.stage,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: assets.scared,
      });

      this.scaredAnim.addEventListener('DOMLoaded', () => {
        if (this.state !== 'scared' || !this.scaredAnim) return;
        this.scaredAnim.playSegments([0, config.scared.totalFrames], true);
      });

      this.scaredAnim.addEventListener('complete', () => {
        if (this.state === 'scared') this.completeSequence(false);
      });
    }

    // `stolen` is true when the steal animation played out to its own
    // natural end (the bird got away with the mug) - the mug is gone for
    // good and the sequence never restarts. When false (the attempt was
    // interrupted), the mug is still sitting there, so the sequence is free
    // to try again after the next idle period.
    completeSequence(stolen) {
      this.state = 'sequence-complete';
      this.destroyBirdAnims();

      if (stolen) {
        this.mugStolen = true;
        this.state = 'done';
        this.teardown();
      } else {
        this.hasBeenScared = true;
        this.state = 'idle-watch';
        this.resetIdleTimer();
      }
    }

    destroyBirdAnims() {
      if (this.stealAnim) {
        this.stealAnim.destroy();
        this.stealAnim = null;
      }
      if (this.scaredAnim) {
        this.scaredAnim.destroy();
        this.scaredAnim = null;
      }
    }

    resetSequence() {
      this.destroyBirdAnims();
      if (this.mugAnim) {
        this.mugAnim.destroy();
        this.mugAnim = null;
      }
      if (this.mugStage) {
        this.mugStage.classList.remove('is-visible');
        this.mugStage.innerHTML = '';
      }
      this.state = 'idle-watch';
      this.resetIdleTimer();
      this.loadInitialMug();
    }

    handleResize() {
      var nowMobile = this.mq.matches;
      var breakpointChanged = nowMobile !== this.isMobile;
      var prevRect = this.eRect;

      this.mobileScrolled = nowMobile && isPageScrolled();
      this.locateE();

      var rectChanged =
        !prevRect ||
        !this.eRect ||
        Math.abs(prevRect.right - this.eRect.right) > 1 ||
        Math.abs(prevRect.bottom - this.eRect.bottom) > 1;

      var sequenceActive = this.state !== 'idle-watch' && this.state !== 'done';

      if (sequenceActive && (breakpointChanged || rectChanged)) {
        this.isMobile = nowMobile;
        this.resetSequence();
        return;
      }

      this.isMobile = nowMobile;
      this.positionHitZone();
      if (this.mugStage.classList.contains('is-visible')) {
        positionMugStage(this.mugStage, this.eRect, this.activeBreakpointConfig());
      }
    }

    teardown() {
      clearTimeout(this.idleTimer);
      if (this.mobileScrollRaf) cancelAnimationFrame(this.mobileScrollRaf);
      ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach((evt) => {
        window.removeEventListener(evt, this.onActivity);
      });
      if (this.onMobileScrollChange) window.removeEventListener('scroll', this.onMobileScrollChange);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.mq && this.onMqChange) this.mq.removeEventListener('change', this.onMqChange);
      if (this.hitZone && this.onHitZoneClick) this.hitZone.removeEventListener('click', this.onHitZoneClick);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.navbarScrollObserver) this.navbarScrollObserver.disconnect();
    }
  }

  customElements.define('home-hero-bird', HomeHeroBirdElement);
})();
